/**
 * The browser's half of an account.
 *
 * Talks to `/auth/*` on THIS origin, which the control server proxies to
 * Supabase. Two reasons that matters: the session cookie and every request stay
 * first party on the production domain, and no page hard-codes the Supabase
 * host, so it stays swappable.
 *
 * **Hand rolled rather than `@supabase/supabase-js`.** The SDK is the standard
 * choice and would be right in a framework app; here the whole surface is four
 * endpoints, the bundles are served as plain esbuild output with no build-time
 * env injection, and the viewer bundle is already 1.2MB. This is the smaller
 * honest thing. The one piece the SDK would give for free is refresh, so that
 * is written out below rather than skipped.
 *
 * **Signing in is never required to publish.** Every page that uses this must
 * work signed out, because anyone in the building may offer a feed. An account
 * buys a handle and an archive, not the right to broadcast.
 */

const STORE = 'sb.session';

interface Stored {
  accessToken: string;
  refreshToken: string;
  /** Epoch ms. Refreshed early rather than on failure. */
  expiresAt: number;
}

export interface Account {
  userId: string;
  email: string | null;
  handle: string | null;
}

function read(): Stored | null {
  try {
    const raw = localStorage.getItem(STORE);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<Stored>;
    if (typeof s.accessToken !== 'string' || typeof s.refreshToken !== 'string') return null;
    return { accessToken: s.accessToken, refreshToken: s.refreshToken, expiresAt: s.expiresAt ?? 0 };
  } catch {
    // A private window, cleared site data, or a storage-blocked browser. Signed
    // out is always a valid answer and never an error.
    return null;
  }
}

function write(s: Stored | null): void {
  try {
    if (s === null) localStorage.removeItem(STORE);
    else localStorage.setItem(STORE, JSON.stringify(s));
  } catch {
    // Not fatal: the session lives for this page instead of across reloads.
  }
}

function store(body: Record<string, unknown>): Stored | null {
  const accessToken = body.access_token;
  const refreshToken = body.refresh_token;
  if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') return null;
  const ttl = typeof body.expires_in === 'number' ? body.expires_in : 3600;
  // Refreshed a minute early, because a token that expires between the check and
  // the request is the failure this exists to avoid.
  const s: Stored = { accessToken, refreshToken, expiresAt: Date.now() + (ttl - 60) * 1000 };
  write(s);
  return s;
}

async function post(path: string, body: unknown): Promise<Response> {
  return fetch(`/auth${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** The message Supabase gave, or a plain one. Never a raw JSON blob at a person. */
async function problem(res: Response): Promise<string> {
  try {
    const b = (await res.json()) as {
      msg?: string; error_description?: string; message?: string; error?: string; fix?: string;
    };
    // `error` and `fix` are this server's shape (the /auth proxy answering for a
    // service it could not reach); the other three are Supabase's. A person
    // needs the sentence, not the status code, and "that did not work (502)"
    // told them nothing they could act on.
    const said = b.msg ?? b.error_description ?? b.message ?? b.error;
    if (said) return b.fix ? `${said}. ${b.fix}` : said;
  } catch {
    // Not JSON. Fall through to the generic answer below.
  }
  if (res.status >= 500) {
    return 'The account service is not answering right now. '
      + 'Try again in a moment. Publishing does not need an account and is unaffected.';
  }
  return `That did not work (${res.status}).`;
}

export async function signUp(email: string, password: string): Promise<string | null> {
  const res = await post('/signup', { email, password });
  if (!res.ok) return problem(res);
  const body = (await res.json()) as Record<string, unknown>;
  // A project with email confirmation on returns a user and NO session. That is
  // success, and saying "signed up, now confirm" beats appearing to fail.
  if (store(body) === null) return 'check your email to confirm the account, then sign in';
  return null;
}

export async function signIn(email: string, password: string): Promise<string | null> {
  const res = await post('/token?grant_type=password', { email, password });
  if (!res.ok) return problem(res);
  return store((await res.json()) as Record<string, unknown>) === null
    ? 'signed in, but no session came back'
    : null;
}

export function signOut(): void {
  write(null);
}

/**
 * A usable access token, refreshing first when it is close to expiry.
 *
 * Returns null when signed out, which every caller must handle: being signed
 * out is the normal state of most people using this.
 */
export async function token(): Promise<string | null> {
  const s = read();
  if (!s) return null;
  if (Date.now() < s.expiresAt) return s.accessToken;
  const res = await post('/token?grant_type=refresh_token', { refresh_token: s.refreshToken });
  if (!res.ok) {
    // A refresh token that no longer works means signed out, and pretending
    // otherwise leaves a page showing an account it cannot act as.
    write(null);
    return null;
  }
  return store((await res.json()) as Record<string, unknown>)?.accessToken ?? null;
}

/** Whether a session exists at all, without a network call. For first paint. */
export function looksSignedIn(): boolean {
  return read() !== null;
}

/**
 * Who is signed in, according to the SERVER.
 *
 * Asked rather than decoded from the token, for the same reason the auto-direct
 * box reads `msg.auto`: a page that reports what it remembers can claim an
 * account the server does not agree it has.
 */
export async function account(): Promise<Account | null> {
  const t = await token();
  if (!t) return null;
  try {
    const res = await fetch('/api/profile', { headers: { Authorization: `Bearer ${t}` } });
    if (!res.ok) return null;
    return (await res.json()) as Account;
  } catch {
    return null;
  }
}
